import { DEFAULT_CURRENCY, INIT_DATA_STORAGE_KEY } from '@/app/constants'
import { UserData } from '@/app/types'
import { calculateDisplayedMaxPrice } from '@/app/utils'
import { useLocalStorage } from '@uidotdev/usehooks'
import { Currencies } from 'currencies-map'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import ReactSlider from 'react-slider'

interface PriceSliderProps {
	onValuesChange: (values: [number, number]) => void
	initialValues: [number, number]
	valuesRange: { min: number; max: number }
}

const PriceSlider: FC<PriceSliderProps> = ({
	onValuesChange,
	initialValues,
	valuesRange,
}) => {
	const [values, setValues] = useState<[number, number]>(initialValues)
	const query = useSearchParams()
	const [initData] = useLocalStorage<UserData>(INIT_DATA_STORAGE_KEY)
	const userCurrency = initData?.selectedCurrency
	const t = useTranslations('Keywords')
	const { min: minPrice, max: maxPrice } = valuesRange

	const currency =
		query.get('currency') || userCurrency?.abbreviation || DEFAULT_CURRENCY

	const currencySymbol = useMemo(
		() => Currencies.symbols.get(currency.toUpperCase()),
		[currency]
	)

	const displayedMaxPrice = useMemo(
		() => calculateDisplayedMaxPrice(minPrice, maxPrice),
		[minPrice, maxPrice]
	)
	useEffect(() => {
		if (initialValues) {
			setValues(initialValues)
		}
	}, [initialValues])

	const handleSliderChange = useCallback((newValues: [number, number]) => {
		setValues(newValues)
	}, [])

	const handleSliderAfterChange = useCallback(
		(newValues: [number, number]) => {
			setValues(newValues)
			onValuesChange([
				newValues[0],
				newValues[1] >= displayedMaxPrice ? maxPrice : newValues[1],
			])
		},
		[displayedMaxPrice, maxPrice, onValuesChange]
	)

	const maxValue = `${currencySymbol}${
		values[1] >= displayedMaxPrice ? `${displayedMaxPrice}+` : values[1]
	}`

	return (
		<div className="w-full">
			<div className="flex justify-between gap-2">
				<div className="flex w-full max-w-64 flex-col items-start px-3 py-1 border border-text-300 rounded-lg">
					<span className="text-xs text-text-700">{t('min')}</span>
					<input
						type="text"
						readOnly
						value={`${currencySymbol}${values[0]}`}
						className="font-medium w-full lg:w-20 xl:w-24 outline-none"
					/>
				</div>
				<div className="flex w-full max-w-64 flex-col items-start px-3 py-1 border border-text-300 rounded-lg">
					<span className="text-xs text-text-700">{t('max')}</span>
					<input
						type="text"
						readOnly
						value={maxValue}
						className="font-medium w-full lg:w-20 xl:w-24 outline-none"
					/>
				</div>
			</div>
			<ReactSlider
				className="horizontal-slider"
				trackClassName="example-track"
				min={minPrice}
				max={displayedMaxPrice}
				value={[values[0], Math.min(values[1], displayedMaxPrice)]}
				onAfterChange={handleSliderAfterChange}
				onChange={handleSliderChange}
				ariaLabel={['Minimum price', 'Maximum price']}
				ariaValuetext={(state) => `${state.valueNow} ${currency}`}
				step={1}
				renderThumb={(props, state) => {
					const { key, ...restProps } = props
					return (
						<div
							key={key}
							{...restProps}
							style={{ ...restProps.style, zIndex: 20 }}
						>
							<div className="price-slider__thumb relative flex cursor-pointer bg-white flex-col items-center w-5 h-5 outline-none top-3 rounded-full border-[6px] border-main" />
						</div>
					)
				}}
				pearling
				minDistance={10}
			/>
		</div>
	)
}

export default PriceSlider
